import { Inject, Injectable, NgZone } from '@angular/core';
import { EMPTY, fromEvent, merge, Subscription, timer } from 'rxjs';
import { startWith, switchMap, tap } from 'rxjs/operators';
import { ENV_CONFIG } from '../injection.token';
import { isNotEmpty } from 'src/app/extensions/utilities';
import {
  DEFAULT_INACTIVITY_TIMEOUT_MINUTES,
  DEFAULT_SESSION_RESUME_TIMEOUT_MINUTES,
  LOGOUT_REASON,
} from 'src/app/core/constants/inactivity';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private userActivityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  private subscription!: Subscription;

  private inactivityMs: number = DEFAULT_INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
  private resumeMs: number = DEFAULT_SESSION_RESUME_TIMEOUT_MINUTES * 60 * 1000;
  private lastActiveKey = 'lastActiveTime';

  constructor(
    private ngZone: NgZone,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData,
    private _oauthService: OAuthService
  ) {
    if (isNotEmpty(this._envConfig.INACTIVITY_TIMEOUT_MINUTES)) {
      this.inactivityMs = Number(this._envConfig.INACTIVITY_TIMEOUT_MINUTES) * 60 * 1000;
    }
    if (isNotEmpty(this._envConfig.SESSION_RESUME_TIMEOUT_MINUTES)) {
      this.resumeMs = Number(this._envConfig.SESSION_RESUME_TIMEOUT_MINUTES) * 60 * 1000;
    }
  }

  startWatching() {
    if (!this.inactivityMs && !this.resumeMs) {
      return;
    }

    this.checkSessionResume();

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(...this.userActivityEvents.map(e => fromEvent(document, e)));

      this.subscription = activity$
        .pipe(
          startWith(null),
          tap(() => this.updateLastActive()),
          switchMap(() => (this.inactivityMs ? timer(this.inactivityMs) : EMPTY))
        )
        .subscribe(() => {
          this.ngZone.run(() => this.logout(LOGOUT_REASON.INACTIVITY));
        });
    });
  }

  private checkSessionResume() {
    if (!this.resumeMs) {
      return;
    }

    const lastActive = localStorage.getItem(this.lastActiveKey);
    if (lastActive) {
      const diff = Date.now() - Number(lastActive);
      if (diff > this.resumeMs) {
        this.logout(LOGOUT_REASON.SESSION_EXPIRED);
      }
    }
  }

  private updateLastActive() {
    localStorage.setItem(this.lastActiveKey, Date.now().toString());
  }

  private logout(reason: LOGOUT_REASON) {
    console.log(`Logging out due to: ${reason}`);
    this._oauthService.logOut();
  }

  stopWatching() {
    this.subscription?.unsubscribe();
  }
}
